import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import heroArtwork from '../assets/stockhunt-hero-v1.png';
import './PublicDashboard.css';

interface MarketData {
  indices: Array<{
    name: string;
    region?: string;
    value: number;
    change: number;
    change_percent: number;
  }>;
  international_indices?: Array<{
    name: string;
    region?: string;
    value: number;
    change: number;
    change_percent: number;
  }>;
  top_gainers: Array<{
    symbol: string;
    change_percent: number;
  }>;
  top_losers: Array<{
    symbol: string;
    change_percent: number;
  }>;
}

type IconName =
  | 'portfolio'
  | 'performance'
  | 'market'
  | 'shield'
  | 'check'
  | 'arrow'
  | 'trend-up'
  | 'trend-down';

const Icon: React.FC<{ name: IconName; className?: string }> = ({ name, className = '' }) => {
  const commonProps = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  switch (name) {
    case 'portfolio':
      return (
        <svg {...commonProps}>
          <rect x="3" y="4" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3 9H21" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8 14H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M8 17H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="17.2" cy="14.4" r="1.8" fill="currentColor" />
        </svg>
      );
    case 'performance':
      return (
        <svg {...commonProps}>
          <path d="M4 19V5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M4 19H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M7 15L11 11L14 13L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16.5 6H20V9.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'market':
      return (
        <svg {...commonProps}>
          <path d="M6 4V20M12 3V17M18 7V21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <rect x="4" y="8" width="4" height="7" rx="1" fill="currentColor" />
          <rect x="10" y="6" width="4" height="7" rx="1" fill="currentColor" opacity="0.72" />
          <rect x="16" y="11" width="4" height="6" rx="1" fill="currentColor" opacity="0.46" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...commonProps}>
          <path d="M12 3L19 6V11.5C19 16 16.2 19.3 12 21C7.8 19.3 5 16 5 11.5V6L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M9 12L11.1 14L15.5 9.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'check':
      return (
        <svg {...commonProps}>
          <path d="M5 12.5L9.2 16.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'trend-up':
      return (
        <svg {...commonProps}>
          <path d="M4 16L9 11L13 14L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 7H20V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'trend-down':
      return (
        <svg {...commonProps}>
          <path d="M4 8L9 13L13 10L20 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 17H20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return (
        <svg {...commonProps}>
          <path d="M5 12H19M14 7L19 12L14 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
  }
};

const features: Array<{ icon: IconName; title: string; text: string; tone: string }> = [
  {
    icon: 'portfolio',
    title: 'One view of your wealth',
    text: 'Bring stocks and mutual funds together in a single, calm portfolio view with current values and cost basis.',
    tone: 'indigo',
  },
  {
    icon: 'performance',
    title: 'Returns that respect time',
    text: 'Understand actual performance with transaction-aware XIRR instead of relying on simple gain percentages.',
    tone: 'emerald',
  },
  {
    icon: 'market',
    title: 'Market context',
    text: 'See Indian market indices, daily movement, gainers, and losers alongside the investments you track.',
    tone: 'cyan',
  },
];

const steps = [
  { number: '01', title: 'Add your holdings', text: 'Record or import the stocks and mutual funds you already own.' },
  { number: '02', title: 'Keep values current', text: 'StockHunt connects your holdings with the latest available market data.' },
  { number: '03', title: 'Make sense of progress', text: 'Review gains, XIRR, allocation, and long-term direction in one place.' },
];

const PublicDashboard: React.FC = () => {
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/market-data/`);
        setMarketData(response.data);
      } catch (err) {
        setError('Live market data is temporarily delayed. Your portfolio tools remain available.');
        console.error('Market data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, []);

  const formatPercent = (percent: number) => `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  const formatValue = (value: number) => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const indices = marketData?.indices?.slice(0, 4) ?? [];
  const gainers = marketData?.top_gainers?.slice(0, 5) ?? [];
  const losers = marketData?.top_losers?.slice(0, 5) ?? [];

  const primaryAction = () => navigate(isAuthenticated ? '/dashboard' : '/register');
  const secondaryAction = () => navigate(isAuthenticated ? '/portfolio' : '/login');

  return (
    <main className="public-home">
      <section className="public-hero" aria-labelledby="public-home-title">
        <div className="public-home__container public-hero__grid">
          <div className="public-hero__copy">
            <div className="public-eyebrow">
              <span className="public-eyebrow__dot" />
              Personal wealth intelligence
            </div>
            <h1 id="public-home-title">
              See your entire wealth story. <span>Clearly.</span>
            </h1>
            <p className="public-hero__lead">
              Track stocks and mutual funds, understand annualized returns based on transaction dates, and follow the market—all from one focused workspace.
            </p>
            <div className="public-hero__actions">
              <button type="button" className="public-button public-button--primary" onClick={primaryAction}>
                {isAuthenticated ? 'Open dashboard' : 'Start tracking free'}
                <Icon name="arrow" />
              </button>
              <button type="button" className="public-button public-button--secondary" onClick={secondaryAction}>
                {isAuthenticated ? 'View portfolio' : 'Sign in'}
              </button>
            </div>
            <div className="public-hero__proof" aria-label="Platform capabilities">
              {['Stocks + mutual funds', 'Transaction-aware XIRR', 'Market context'].map((item) => (
                <span key={item}><Icon name="check" />{item}</span>
              ))}
            </div>
          </div>

          <div className="public-hero__visual">
            <div className="public-hero__halo" aria-hidden="true" />
            <img
              src={heroArtwork}
              alt="Abstract portfolio analytics showing allocation charts and a rising growth curve"
              loading="eager"
              decoding="async"
            />
            <div className="public-float-card public-float-card--top" aria-hidden="true">
              <span className="public-float-card__icon"><Icon name="shield" /></span>
              <span><small>Built for</small><strong>Personal clarity</strong></span>
            </div>
            <div className="public-float-card public-float-card--bottom" aria-hidden="true">
              <span className="public-live-dot" />
              <span><small>Market pulse</small><strong>Latest available data</strong></span>
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-features" id="features" aria-labelledby="features-title">
        <div className="public-home__container">
          <div className="public-section__intro">
            <div>
              <span className="public-section__kicker">Clarity, not clutter</span>
              <h2 id="features-title">Everything important. Nothing distracting.</h2>
            </div>
            <p>StockHunt turns scattered investment records into a reliable view of what you own, how it is performing, and where it may be heading.</p>
          </div>

          <div className="public-feature-grid">
            {features.map((feature) => (
              <article className="public-feature-card" key={feature.title}>
                <div className={`public-feature-card__icon public-feature-card__icon--${feature.tone}`}>
                  <Icon name={feature.icon} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
                <span className="public-feature-card__link">Explore the platform <Icon name="arrow" /></span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section public-market" id="markets" aria-labelledby="market-title">
        <div className="public-home__container">
          <div className="public-section__intro public-section__intro--market">
            <div>
              <span className="public-section__kicker">Market pulse</span>
              <h2 id="market-title">The market, in context.</h2>
            </div>
            <div className={`public-market-status ${error ? 'public-market-status--delayed' : ''}`} role="status">
              <span />
              {loading ? 'Updating market data' : error ? 'Data delayed' : 'Latest available market data'}
            </div>
          </div>

          {error && <div className="public-market-notice">{error}</div>}

          <div className="public-index-grid" aria-busy={loading}>
            {loading && [0, 1, 2, 3].map((item) => (
              <div className="public-index-card public-index-card--loading" key={item} aria-hidden="true">
                <span /><strong /><i />
              </div>
            ))}
            {!loading && indices.map((index) => {
              const isPositive = index.change >= 0;
              return (
                <article className="public-index-card" key={index.name}>
                  <div className="public-index-card__header">
                    <span>{index.name}</span>
                    <span className={isPositive ? 'is-positive' : 'is-negative'}>
                      <Icon name={isPositive ? 'trend-up' : 'trend-down'} />
                    </span>
                  </div>
                  <strong>{formatValue(index.value)}</strong>
                  <div className="public-index-card__footer">
                    <span className={isPositive ? 'is-positive' : 'is-negative'}>{formatPercent(index.change_percent)}</span>
                    <svg viewBox="0 0 96 28" preserveAspectRatio="none" aria-hidden="true">
                      <path
                        d={isPositive ? 'M2 24 C14 22, 18 15, 29 17 S44 20, 54 11 S70 15, 94 3' : 'M2 5 C15 7, 20 15, 31 11 S47 8, 57 18 S72 14, 94 25'}
                        className={isPositive ? 'spark-positive' : 'spark-negative'}
                      />
                    </svg>
                  </div>
                </article>
              );
            })}
            {!loading && indices.length === 0 && (
              <div className="public-market-empty">Market indices will appear here when the data service reconnects.</div>
            )}
          </div>

          <div className="public-movers-grid">
            <article className="public-movers-card">
              <header>
                <div>
                  <span className="public-movers-card__icon public-movers-card__icon--positive"><Icon name="trend-up" /></span>
                  <span><small>Momentum</small><strong>Top gainers</strong></span>
                </div>
                <span className="public-movers-card__period">Today</span>
              </header>
              <div className="public-movers-card__list">
                {loading && <div className="public-list-loading">Loading latest movement…</div>}
                {!loading && gainers.map((stock, index) => (
                  <div className="public-mover-row" key={`${stock.symbol}-${index}`}>
                    <span className="public-mover-row__rank">{String(index + 1).padStart(2, '0')}</span>
                    <strong>{stock.symbol}</strong>
                    <span className="is-positive">{formatPercent(stock.change_percent)}</span>
                  </div>
                ))}
                {!loading && gainers.length === 0 && <div className="public-list-loading">No gainers available.</div>}
              </div>
            </article>

            <article className="public-movers-card">
              <header>
                <div>
                  <span className="public-movers-card__icon public-movers-card__icon--negative"><Icon name="trend-down" /></span>
                  <span><small>Movement</small><strong>Top losers</strong></span>
                </div>
                <span className="public-movers-card__period">Today</span>
              </header>
              <div className="public-movers-card__list">
                {loading && <div className="public-list-loading">Loading latest movement…</div>}
                {!loading && losers.map((stock, index) => (
                  <div className="public-mover-row" key={`${stock.symbol}-${index}`}>
                    <span className="public-mover-row__rank">{String(index + 1).padStart(2, '0')}</span>
                    <strong>{stock.symbol}</strong>
                    <span className="is-negative">{formatPercent(stock.change_percent)}</span>
                  </div>
                ))}
                {!loading && losers.length === 0 && <div className="public-list-loading">No losers available.</div>}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="public-section public-how" aria-labelledby="how-title">
        <div className="public-home__container">
          <div className="public-how__header">
            <span className="public-section__kicker">A simpler workflow</span>
            <h2 id="how-title">From holdings to insight in three steps.</h2>
          </div>
          <div className="public-step-grid">
            {steps.map((step) => (
              <article className="public-step" key={step.number}>
                <span className="public-step__number">{step.number}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="public-section public-trust" id="security" aria-labelledby="trust-title">
        <div className="public-home__container public-trust__panel">
          <div className="public-trust__icon"><Icon name="shield" /></div>
          <div className="public-trust__copy">
            <span className="public-section__kicker">Built for personal tracking</span>
            <h2 id="trust-title">Your financial picture deserves a focused workspace.</h2>
            <p>StockHunt keeps portfolio monitoring, market context, and performance calculations together so you can verify the numbers that matter without spreadsheet sprawl.</p>
          </div>
          <div className="public-trust__points">
            <span><Icon name="check" />Authenticated portfolio access</span>
            <span><Icon name="check" />Clear calculation breakdowns</span>
            <span><Icon name="check" />Broker-ledger cross-check guidance</span>
          </div>
        </div>
      </section>

      {!isAuthenticated && (
        <section className="public-section public-cta" aria-labelledby="cta-title">
          <div className="public-home__container">
            <div className="public-cta__panel">
              <div>
                <span className="public-section__kicker">Your next clear decision starts here</span>
                <h2 id="cta-title">Bring your portfolio into focus.</h2>
                <p>Create your StockHunt account and build a clearer view of your investments.</p>
              </div>
              <button type="button" className="public-button public-button--light" onClick={() => navigate('/register')}>
                Create your account <Icon name="arrow" />
              </button>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default PublicDashboard;
